import { call, put, race, select, take, takeEvery } from 'redux-saga/effects'

import {
  FETCH_RESOURCE_REQUEST,
  fetchResourceSuccess,
  fetchResourceFailure,
} from '../actions/resource'

import { addFlashMessage } from '../actions/flashMessage'
import { SET_CURRENT_PROJECT } from '../actions/currentProject'
import { SIGN_OUT_SUCCESS } from '../actions/authorization'
import { getCurrentProjectId } from '../reducers'

function* fetchModules(api, resource, payload) {
  if (resource.operation !== 'list') {
    return yield call(api.fetch, resource, payload)
  }
  const search = new URLSearchParams(resource.params?.search || '')
  // Explicitly paginated callers keep their requested page. Full-project loads
  // must fetch every page: the unpaginated API otherwise stops at 1,000 modules.
  if (search.has('page')) return yield call(api.fetch, resource, payload)
  search.set('per_page', '1000')
  const json = []
  let totalPages = 1
  for (let page = 1; page <= totalPages; page += 1) {
    search.set('page', page)
    const response = yield call(
      api.fetch,
      {
        ...resource,
        params: { ...resource.params, search: search.toString() },
      },
      payload
    )
    json.push(...response.json)
    totalPages = response.meta?.totalPages || 1
  }
  return { json, meta: {} }
}

// Full module lists that are currently loading, by project and search. The map
// and the project switch saga request the same list at the same time.
const pendingModuleLists = new Map()

function* fetchModulesOnce(api, resource, payload, projectId) {
  if (resource.operation !== 'list') {
    return yield call(fetchModules, api, resource, payload)
  }
  const key = `${projectId}|${resource.params?.search || ''}`
  const pending = pendingModuleLists.get(key)
  // A fresh request (after a reconnect) must not reuse a response that may have
  // been read before the missed changes.
  if (pending && !resource.params?.fresh) {
    return yield call(() => pending)
  }

  let settle
  const shared = new Promise((resolve, reject) => {
    settle = { resolve, reject }
  })
  shared.catch(() => {})
  pendingModuleLists.set(key, shared)
  try {
    const result = yield call(fetchModules, api, resource, payload)
    settle.resolve(result)
    return result
  } catch (error) {
    settle.reject(error)
    throw error
  } finally {
    // Also settles on cancellation, so waiting requests do not hang. A no-op
    // when already settled.
    settle.reject(new Error('Module list request cancelled'))
    if (pendingModuleLists.get(key) === shared) pendingModuleLists.delete(key)
  }
}

function* fetchResource(api, action) {
  const { resource, payload } = action
  let onSuccess, onError
  action.promise = new Promise((resolve, reject) => {
    onSuccess = resolve
    onError = reject
  })
  try {
    let result
    if (
      resource.name === 'modules' &&
      ['list', 'read'].includes(resource.operation)
    ) {
      const projectId = yield select(getCurrentProjectId)
      if (
        projectId == null ||
        (resource.params?.projectId != null &&
          resource.params.projectId !== projectId)
      ) {
        yield put(fetchResourceFailure(resource, { cancelled: true }))
        onSuccess()
        return
      }
      const response = yield race({
        result: call(fetchModulesOnce, api, resource, payload, projectId),
        scopeChanged: take(
          (action) =>
            action.type === SIGN_OUT_SUCCESS ||
            (action.type === SET_CURRENT_PROJECT &&
              action.payload !== projectId)
        ),
      })
      const currentProjectId = yield select(getCurrentProjectId)
      if (response.scopeChanged || currentProjectId !== projectId) {
        yield put(fetchResourceFailure(resource, { cancelled: true }))
        onSuccess()
        return
      }
      result = response.result
    } else {
      result = yield call(api.fetch, resource, payload)
    }
    yield put(fetchResourceSuccess(resource, result))
    onSuccess(result)
  } catch (error) {
    // console.error(error)
    yield put(fetchResourceFailure(resource, error))
    onSuccess(error)
    if (error.status !== 403) {
      yield put(addFlashMessage('FetchError', 'Fehler: ' + error))
    }
  }
}

export default function* watchFetch(api) {
  yield takeEvery(FETCH_RESOURCE_REQUEST, fetchResource, api)
}
